import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable /*, NgZone */ } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService } from '@progress/kendo-angular-notification';
import {
  Observable,
  /* Observer, */
  catchError,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { Helpers } from '../models/helpers';
import { IWsResult } from '../models/ws_result.model';
import { AuthService } from '../services/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(
    private router: Router,
    private authServ: AuthService,
    private notificationService: NotificationService,
    // private ngZone: NgZone,   // ↓ riattivare insieme al wrapper in coda se serve
  ) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const currentUser = this.authServ.currentUserValue;
    const isLoggedIn = !!currentUser?.ac_token;
    const isApiUrl = request.url.startsWith(environment.urlServer);
    const skipTokenInjection = request.headers.has('token');
    const isFormData = request.body instanceof FormData;

    // Header da aggiungere. Content-Type viene impostato solo per body
    // non-FormData: con FormData il browser gestisce gia' boundary e
    // multipart/form-data, sovrascriverlo rompe gli upload.
    const setHeaders: Record<string, string> = {};
    if (!isFormData) {
      setHeaders['Content-Type'] = 'application/json';
    }
    if (isLoggedIn && isApiUrl && !skipTokenInjection) {
      setHeaders['token'] = `${currentUser.ac_token}`;
    }

    if (Object.keys(setHeaders).length > 0) {
      request = request.clone({ setHeaders });
    }

    const handled$ = next.handle(request).pipe(
      switchMap((res) => {
        if (
          res instanceof HttpResponse &&
          this.instanceOfIWsResult(res.body) &&
          res.body.error_message?.msg_code === 7
        ) {
          // msg_code 7 = token scaduto.
          const isScreenAccount =
            this.authServ.currentUserValue?.ac_user_type_cod ===
            Helpers.PermisionScreen;

          if (isScreenAccount) {
            // Account Screen (kiosk incustodito, es. monitor magazzino):
            // MAI logout/redirect a /login, che lo lascerebbe bloccato
            // finche' qualcuno non va fisicamente a rifare login.
            // Recupero in due tentativi:
            //   1. si riapplica la sessione kiosk hardcoded (token
            //      statico, sempre valido lato backend) e si ripete la
            //      richiesta;
            //   2. se anche quella risponde msg_code 7, re-login con le
            //      credenziali salvate (`reloginScreen`).
            // Se fallisce tutto: solo notifica, il monitor resta dov'e'.
            return this.recoverScreenSession(request, next, res);
          }

          this.showNotification(
            'Token scaduto, effettua nuovamente il login!',
            'warning',
          );
          this.authServ.logout();
        }
        return of(res);
      }),
      catchError((err: HttpErrorResponse) => {
        // Gli errori HTTP devono propagarsi al consumer perche' possa
        // mostrare un messaggio o reagire. Storicamente l'interceptor
        // li trasformava in 'of(err)' (success), causando UI vuote senza
        // alcun feedback. Da T6 in poi: rilancio reale dell'errore.
        return throwError(() => err);
      }),
    );

    return handled$;

    // -------------------------------------------------------------------
    // Safety net: re-bridge in NgZone.
    //
    // Angular 19+ con Fetch backend (provideHttpClient(withFetch())) puo'
    // far arrivare le callback HTTP fuori dalla Angular zone, impedendo il
    // change detection automatico. Qui sotto il wrapper storico che forza
    // ogni emit dentro NgZone.
    //
    // STATO ATTUALE: l'app usa il backend XHR classico (provideHttpClient
    // senza withFetch in app.module.ts), quindi il wrapper NON e' necessario
    // e viene tenuto solo come fallback. Se si vede una UI che "non si
    // aggiorna" dopo una chiamata HTTP, riattivare:
    //   1. l'import NgZone in cima
    //   2. l'import Observer da rxjs
    //   3. il parametro `private ngZone: NgZone` nel constructor
    //   4. sostituire `return handled$;` qui sopra con il blocco sotto
    //
    // return new Observable((observer: Observer<HttpEvent<any>>) => {
    //   return handled$.subscribe({
    //     next: (val) => this.ngZone.run(() => observer.next(val)),
    //     error: (err) => this.ngZone.run(() => observer.error(err)),
    //     complete: () => this.ngZone.run(() => observer.complete()),
    //   });
    // });
  }

  /**
   * Recupero sessione per account Screen dopo un msg_code 7. Vedi il
   * commento nel branch `isScreenAccount` sopra. Non chiama MAI
   * `logout()`: l'obiettivo e' che il monitor non risulti mai scollegato.
   */
  private recoverScreenSession(
    request: HttpRequest<any>,
    next: HttpHandler,
    originalRes: HttpResponse<any>,
  ): Observable<HttpEvent<any>> {
    this.authServ.applyKioskSession();
    const kioskReq = request.clone({
      setHeaders: { token: `${this.authServ.currentUserValue?.ac_token}` },
    });
    return next.handle(kioskReq).pipe(
      switchMap((kioskRes) => {
        const stillExpired =
          kioskRes instanceof HttpResponse &&
          this.instanceOfIWsResult(kioskRes.body) &&
          kioskRes.body.error_message?.msg_code === 7;
        if (!stillExpired) {
          return of(kioskRes);
        }
        return this.authServ.reloginScreen().pipe(
          switchMap((success) => {
            if (success) {
              const retryReq = request.clone({
                setHeaders: {
                  token: `${this.authServ.currentUserValue?.ac_token}`,
                },
              });
              return next.handle(retryReq);
            }
            this.showNotification(
              'Sessione monitor non ripristinabile, contattare un amministratore!',
              'warning',
            );
            return of(kioskRes);
          }),
        );
      }),
    );
  }

  private instanceOfIWsResult(object: unknown): object is IWsResult {
    return typeof object === 'object' && object !== null && 'data' in object;
  }

  public showNotification(
    messageText: string,
    styleType: 'none' | 'success' | 'warning' | 'error' | 'info',
  ): void {
    this.notificationService.show({
      content: messageText,
      hideAfter: 600,
      position: { horizontal: 'center', vertical: 'top' },
      animation: { type: 'fade', duration: 2000 },
      type: { style: styleType, icon: true },
    });
  }

}
